/**
 * MultiSelectCombobox — generic Layer-1 multi-select built on Base UI Combobox.
 *
 * The reusable picker for any closed/fixed list with multiple selection (the
 * country field's single-select sibling). Selected items render as removable
 * chips inside the input; the dropdown lists every option and the chips-input
 * filters by typing. Emits values (not labels), deduped and ordered to match
 * `options` order — so persistence is independent of click order.
 *
 * @layer-1 — shared themed wrapper. Reuse for every multi-select-from-list field.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Combobox,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
  ComboboxValue,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { cn } from "@/shared/lib/utils";

/**
 * Chip container + chip styling matched to the SUBJECTS TagEditor (see
 * TitleDetailPage) so every multi-select reads as the same light tag field:
 * full-width rounded box, light `--surface-low` chips with `--border-light`.
 * Overrides the shadcn base (dark `bg-muted`, tight `min-h-8`) via cn/tw-merge.
 * RTL-safe: padding uses logical px-* and the X sits at the chip's inline-end.
 */
const CHIPS_CONTAINER =
  "w-full flex-wrap items-center gap-1.5 rounded-[14px] px-3 py-2 min-h-[46px] cursor-pointer border-[var(--input-border)] bg-[var(--background)] dark:bg-[var(--background)] " +
  // Match ThemedInput focus/hover (dark border, no gold ring) instead of the
  // shadcn base's gold `--ring` glow.
  "hover:border-[var(--foreground)] focus-within:border-[var(--foreground)] focus-within:ring-0";
/** Dropdown option row — mirrors ThemedCombobox's OptionItem (taller, ps-4/pe-9,
 *  same text size + highlight) so single/multi selects read identically. */
const ITEM_CLS =
  "min-h-12 items-center gap-2 py-2 ps-4 pe-9 text-[13.5px] text-[var(--foreground)] " +
  "data-highlighted:bg-[var(--card-hover)] data-highlighted:text-[var(--foreground)]";
const CHIP =
  "h-auto rounded-md border border-[var(--border-light)] bg-[var(--surface-low)] px-[10px] py-1 text-[12px] font-medium text-[var(--text-secondary)]";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectComboboxProps {
  options: ReadonlyArray<MultiSelectOption>;
  /** Selected values (subset of options' values). */
  value: string[];
  /** Receives the new selection, deduped and reordered to `options` order. */
  onChange: (values: string[]) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/**
 * Generic multi-select combobox with chips.
 *
 * Flow: maps the `value` string[] to the matching option objects for Base UI's
 * controlled `value`, renders one removable chip per selected option, and on any
 * change re-emits the values sorted by `options` order (canonical) and deduped.
 */
export function MultiSelectCombobox({
  options,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  id,
  className,
}: MultiSelectComboboxProps) {
  const { t } = useTranslation("common");

  /** Anchor the dropdown to the full chips container — not the shrinking input —
   *  so its width stays locked to the field and aligned to the start edge. */
  const anchorRef = useComboboxAnchor();

  const order = useMemo(
    () => new Map<string, number>(options.map((o, i) => [o.value, i])),
    [options],
  );

  // Controlled value as option objects (stable references from `options`).
  const selected = useMemo(
    () =>
      value
        .map((v) => options.find((o) => o.value === v))
        .filter((o): o is MultiSelectOption => Boolean(o)),
    [value, options],
  );

  const emit = (items: readonly MultiSelectOption[]) => {
    const vals = [...new Set(items.map((o) => o.value))].sort(
      (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0),
    );
    onChange(vals);
  };

  return (
    <Combobox
      items={options as MultiSelectOption[]}
      multiple
      value={selected}
      onValueChange={emit}
      itemToStringLabel={(o: MultiSelectOption | null) => o?.label ?? ""}
      itemToStringValue={(o: MultiSelectOption | null) => o?.value ?? ""}
      disabled={disabled}
    >
      <ComboboxChips
        ref={anchorRef}
        className={cn(CHIPS_CONTAINER, className)}
        aria-invalid={error || undefined}
      >
        <ComboboxValue>
          {(items: MultiSelectOption[]) =>
            items.map((o) => (
              <ComboboxChip key={o.value} className={CHIP} aria-label={o.label}>
                {o.label}
              </ComboboxChip>
            ))
          }
        </ComboboxValue>
        <ComboboxChipsInput
          id={id}
          disabled={disabled}
          placeholder={selected.length ? "" : placeholder}
          className="cursor-pointer"
        />
      </ComboboxChips>
      <ComboboxContent anchor={anchorRef} align="start" side="bottom" sideOffset={6}>
        <ComboboxEmpty>{t("common.noResults")}</ComboboxEmpty>
        <ComboboxList>
          {(o: MultiSelectOption) => (
            <ComboboxItem key={o.value} value={o} className={ITEM_CLS}>
              {o.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
